// Pure label/value resolution helpers for RadioSnapshotView.tsx (Settings >
// Node Configuration > Live snapshot rebuild, 2026-09-22) — kept separate
// from the component file so the presentation-vs-resolution concerns don't
// blur, same split this app already uses elsewhere (definitions.ts vs the
// components that consume it). Nothing here renders JSX; it only decides
// what label/value a raw payload key should show as.

import { configFieldLabel, configFieldRange, type ConfigFieldRange } from './definitions/settings';
import type { MeshtasticField } from "./meshtasticSchema.generated";

// The backend's own literal redaction marker (meshtastic_tcp.py
// _REDACT_CONFIG_KEYS, same constant MeshtasticValuesForm.tsx's
// REDACTED_PLACEHOLDER guards against on the write side) — a real value to
// render, never blank and never a guessed/fake replacement.
export const REDACTED = "<redacted>";

export function isRedacted(value: unknown): boolean {
  return value === REDACTED;
}

// camelCase (protobuf jsonName, e.g. "hasPKC", "positionPrecision") ->
// snake_case, for the two places this app needs to go from a live
// MessageToDict key to the bare-name form configFieldLabel/CONFIG_FIELD_LABEL
// key on: fields with no meshtasticSchema.generated.ts entry at all
// (device.metadata, module_config.*'s moduleSettings.* leaves). Handles a
// run of capitals (e.g. "hasPKC" -> "has_pkc") the same way Meshtastic's own
// jsonName casing does, not just a single-capital boundary.
export function camelToSnake(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();
}

// The unit-double-up guard the task brief calls for: some existing labels
// already bake their unit into the text (e.g. "TX power (dBm)") — if so,
// never append a second "(unit)" from configFieldRange.
function labelAlreadyHasUnit(label: string): boolean {
  return /\([^)]*\)\s*$/.test(label);
}

export function appendUnit(label: string, range: ConfigFieldRange | undefined): string {
  if (!range?.unit || labelAlreadyHasUnit(label)) return label;
  return `${label} (${range.unit})`;
}

export function meshtasticFieldByJsonName(fields: readonly MeshtasticField[] | undefined, jsonName: string): MeshtasticField | undefined {
  return fields?.find((f) => f.jsonName === jsonName);
}

// A resolved row's label + how its value should render, worked out once
// here rather than scattered across the component. `raw` is kept alongside
// the resolved bits so a caller can still fall back to it (e.g. for the
// mono/monospace judgment call).
export interface ResolvedField {
  label: string;
  isEnum: boolean;
  enumType?: string;
  range?: ConfigFieldRange;
}

// Resolution chain for a live Meshtastic device.config.*/module_config.*
// key (task brief's own wording): camelCase jsonName -> find the
// MeshtasticField in that section by jsonName -> field.name feeds
// configFieldLabel/configFieldRange, field.enumType flags it for
// configEnumLabel. Falls back to the camelCase->snake_case->configFieldLabel
// path (still never blank) when the section has no matching field at all —
// e.g. a channel's moduleSettings.* leaves, which aren't expanded in
// meshtasticSchema.generated.ts.
export function resolveMeshtasticKey(fields: readonly MeshtasticField[] | undefined, jsonKey: string): ResolvedField {
  const field = meshtasticFieldByJsonName(fields, jsonKey);
  if (field) {
    const label = configFieldLabel(field.name);
    return {
      label: field.kind === "enum" ? label : appendUnit(label, configFieldRange(field.name)),
      isEnum: field.kind === "enum",
      enumType: field.enumType,
      range: configFieldRange(field.name),
    };
  }
  const snake = camelToSnake(jsonKey);
  return { label: configFieldLabel(snake), isEnum: false };
}

export function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return "—"; // em dash — genuinely absent
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  return String(value);
}

// Position source values are part of the RadioConfigEntry contract itself
// (backend-authored, not a Meshtastic/MeshCore protobuf enum), so this is a
// small local map rather than an addition to definitions.ts's shared
// config-schema registries.
const POSITION_SOURCE_LABEL: Record<string, string> = {
  configured: "Configured",
  broadcast: "Broadcast (advertised over the air)",
  none: "None reported",
};

export function positionSourceLabel(raw: string | null | undefined): string {
  if (!raw) return POSITION_SOURCE_LABEL.none;
  return POSITION_SOURCE_LABEL[raw] ?? raw;
}

export function formatLatLon(pos: { lat: number; lon: number } | null | undefined): string | null {
  if (!pos || pos.lat == null || pos.lon == null) return null;
  return `${pos.lat}, ${pos.lon}`;
}
